import { ReportChartConfiguration, ReportChartFilterItemTypes, ReportChartMeasureType } from '@vision/ui/interfaces';
import { TestContext, array, object, string } from 'yup';
import { BarChart4CompareRenderer, BarChart4MeasureRenderer } from '../../components';
import {
  DefaultReportChartCompareSchema,
  DefaultReportChartThemeFont,
  DefaultReportChartThemeSeries,
  extendDefaultChartValidationSchema,
} from '../default-chart.config';
import { BarChart4Filtres } from './bar-chart4.filtre';
import { BarChart4Measures } from './bar-chart4.measures';

const VALIDATION_SCHEMA = {
  selectedCompareCriteria: object().shape({
    ...DefaultReportChartCompareSchema,
  }),
  selectedCompare: array()
    .of(string())
    .when('selectedMeasure', {
      is: (value: string) => !['matrix-question-csat-overview'].includes(value),
      then: (field) => field.min(1, 'validation:rules.generic.required'),
    }),
  selectedFilters: object().shape({
    selectedQuestionIds: array()
      .of(string())
      .test(
        'selectedCompareCriteria',
        'validation:rules.generic.required',
        function (value: any, context: TestContext) {
          const selectedMeasure = context.from[1]?.value['selectedMeasure'];
          if (['matrix-question-csat-overview'].includes(selectedMeasure) && value.length === 0) {
            return false;
          }
          return true;
        },
      ),
  }),
};

export const BarChart4DefaultConfiguration: ReportChartConfiguration = {
  supportsLegendSettings: true,
  validationSchema: extendDefaultChartValidationSchema({
    params: VALIDATION_SCHEMA,
    withCompareCriteria: false,
    withFeedbackBasedQuestionBased: true,
  }),
  measures: BarChart4Measures,
  compareRendererComponent: BarChart4CompareRenderer,
  measureRendererComponent: BarChart4MeasureRenderer,
  compareMultiple: true,
  hideCompareCondition: (values) => ['matrix-question-csat-overview'].includes(values.selectedMeasure),
  customize: {
    active: (values) => {
      // matrix-question-csat-overview seçildiğinde compare seçilmiyor.
      if (['matrix-question-csat-overview'].includes(values.selectedMeasure)) {
        return values.selectedFilters.selectedQuestionIds.length > 0;
      }

      return values.selectedMeasure && values.selectedCompare.length > 0;
    },
    transactionalDate: {
      active: false,
    },
    feedbackCount: {
      active: false,
    },
    sortResults: {
      limit: 0,
      order: null,
    },
    limits: {
      upperLimitActive: false,
      upperLimit: 0,
      lowerLimitActive: false,
      lowerLimit: 0,
    },
  },
  filters: (measureType: ReportChartMeasureType) => {
    for (const item of BarChart4Filtres) {
      if (item.measures.includes(measureType)) {
        return item.filterItems;
      }
    }
    return ReportChartFilterItemTypes;
  },
  defaultTheme: {
    active: (values) => {
      // matrix-question-csat-overview seçildiğinde compare seçilmiyor.
      if (['matrix-question-csat-overview'].includes(values.selectedMeasure)) {
        return values.selectedFilters.selectedQuestionIds.length > 0;
      }

      return values.selectedMeasure && values.selectedCompare.length > 0;
    },
    title: {
      ...DefaultReportChartThemeFont,
      label: '',
    },
    xAxis: {
      ...DefaultReportChartThemeFont,
    },
    yAxis: {
      ...DefaultReportChartThemeFont,
      activeDisplayFormat: true,
      displayFormat: 'auto',
      minMaxValue: {
        minValue: null,
        maxValue: null,
      },
    },
    dataLabels: {
      ...DefaultReportChartThemeFont,
      active: false,
      alignment: 'horizontal',
      displayFormat: 'auto',
      roundingMethod: 'default',
      labelDisplayFormat: 'number',
      position: 'top',
    },
    series: {
      ...DefaultReportChartThemeSeries,
    },
  },
};
